// Iter68 — Partners admin converted to card grid layout (mirrors
// Product Management visual). Preserves search/filter/approval logic.

import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FileCheck, Package, BarChart3, Search } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProfileCard, ProfileCardGrid } from "@/components/admin/ProfileCard";

const STATUS_TONE = {
  pending:   { label: "Pending",   tone: "amber" },
  approved:  { label: "Approved",  tone: "green" },
  suspended: { label: "Suspended", tone: "rose" },
  rejected:  { label: "Rejected",  tone: "rose" },
};

export default function AdminPartnersHome() {
  const [profiles, setProfiles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  // Iter74 — bulk select + delete
  const [selected, setSelected] = useState(new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/partners/profiles"),
        api.get("/admin/partners/documents?status=pending"),
      ]);
      setProfiles(a.data); setDocs(b.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const setCompliance = async (id, status) => {
    await api.put(`/admin/partners/profiles/${id}/compliance`, { status });
    await reload();
  };
  const review = async (id, status) => {
    await api.put(`/admin/partners/documents/${id}/review`, { status });
    await reload();
  };

  // Iter74 — repair a partner that was approved before the logo-copy fix
  const repair = async (p) => {
    setErr(""); setOk("");
    try {
      await api.post(`/admin/partners/${p.user_id}/repair-from-application`, {});
      setOk(`Repaired ${p.business_name || p.slug} from their application.`);
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // Iter74 — bulk delete (hard)
  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const doBulkDelete = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.delete("/admin/partners/bulk-delete",
        { data: { ids: Array.from(selected) } });
      setOk(`Deleted ${data.deleted_count}. Skipped ${data.skipped_count} (protected).`);
      setSelected(new Set()); setConfirmDel(false);
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return profiles.filter((p) => {
      if (statusF && p.compliance?.status !== statusF) return false;
      if (!ql) return true;
      return (
        (p.business_name || "").toLowerCase().includes(ql) ||
        (p.slug || "").toLowerCase().includes(ql) ||
        (p.business_category || "").toLowerCase().includes(ql)
      );
    });
  }, [profiles, q, statusF]);

  if (err && profiles.length === 0) return <div className="text-rose-300">{err}</div>;

  return (
    <div data-testid="admin-partners-home" className="space-y-6">
      <header className="fade-up flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Partner admin</div>
          <h1 className="headline-xl">Partners</h1>
          <p className="text-lifted-dim mt-2 max-w-2xl text-sm">
            Compliance, fulfillment, and metrics. Partner orders flow directly into the existing{" "}
            <Link to="/admin/delivery/dispatch" className="text-lifted-green underline">dispatch board</Link>.
          </p>
        </div>
        {selected.size > 0 && (
          <button
            data-testid="admin-partners-bulk-delete-btn"
            onClick={() => setConfirmDel(true)}
            className="btn-lifted !py-2 !px-3 !text-xs bg-rose-500/80 hover:bg-rose-500 inline-flex items-center gap-1.5"
          >Delete Selected ({selected.size})</button>
        )}
      </header>

      {err && <div className="text-rose-300 text-sm" data-testid="admin-partners-err">{err}</div>}
      {ok &&  <div className="text-lifted-green text-sm" data-testid="admin-partners-ok">{ok}</div>}

      {/* Filter bar */}
      <div className="glass-panel p-3 flex flex-wrap items-center gap-3" data-testid="admin-partners-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-lifted-dim" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by business, slug, category…"
            className="w-full bg-black/40 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
            data-testid="admin-partners-search"
          />
        </div>
        <select
          value={statusF}
          onChange={(e) => setStatusF(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
          data-testid="admin-partners-status-filter"
        >
          <option value="">All compliance</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="suspended">Suspended</option>
          <option value="rejected">Rejected</option>
        </select>
        <div className="text-xs text-lifted-dim ml-auto" data-testid="admin-partners-count">
          {filtered.length} of {profiles.length}
        </div>
      </div>

      {/* Profile cards */}
      <ProfileCardGrid testId="admin-partners-grid">
        {filtered.map((p) => {
          const status = p.compliance?.status || "pending";
          const tone = STATUS_TONE[status] || { label: status, tone: "amber" };
          return (
            <div key={p.id} className="relative">
              {/* Iter74 — bulk select checkbox */}
              <label className="absolute top-2 left-2 z-10 bg-black/70 rounded p-1 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`admin-partner-${p.id}-checkbox`}
                  checked={selected.has(p.id)}
                  onChange={() => toggleSel(p.id)}
                />
              </label>
              <ProfileCard
                testId={`admin-partner-${p.id}`}
                image={p.logo_url}
                topRightBadge={tone.label}
                title={p.business_name || p.slug}
                subtitle={p.business_category || "Partner"}
                meta={p.primary_address}
                chips={[
                  { label: tone.label, tone: tone.tone },
                  p.business_category && { label: p.business_category, tone: "purple" },
                ].filter(Boolean)}
                stats={[
                  { label: "Orders", value: p.stats?.orders_completed ?? 0 },
                  { label: "Revenue", value: `$${((p.stats?.total_revenue_cents || 0) / 100).toFixed(0)}` },
                  { label: "Slug", value: (p.slug || "—").slice(0, 8) },
                ]}
                footer={
                  <div className="inline-flex gap-1.5 w-full flex-wrap">
                    {status !== "approved" && (
                      <button
                        onClick={() => setCompliance(p.id, "approved")}
                        className="btn-lifted !py-1.5 !px-2 !text-[0.6rem] flex-1"
                        data-testid={`admin-partner-${p.id}-approve`}
                      >Approve</button>
                    )}
                    {status === "approved" && (
                      <button
                        onClick={() => setCompliance(p.id, "suspended")}
                        className="btn-ghost !py-1.5 !px-2 !text-[0.6rem] flex-1 hover:!text-rose-300"
                        data-testid={`admin-partner-${p.id}-suspend`}
                      >Suspend</button>
                    )}
                    <button
                      onClick={() => repair(p)}
                      className="btn-ghost !py-1.5 !px-2 !text-[0.6rem]"
                      data-testid={`admin-partner-${p.id}-repair`}
                      title="Re-copy logo/address/business from this partner's application"
                    >Repair</button>
                    <Link
                      to={`/shop/partner/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost !py-1.5 !px-2 !text-[0.6rem]"
                    >View</Link>
                  </div>
                }
              />
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full glass-panel p-8 text-center text-lifted-mute">
            No partners match this filter.
          </div>
        )}
      </ProfileCardGrid>

      {/* Pending documents */}
      <section className="glass-panel p-5" data-testid="admin-partners-doc-review">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2 mb-3"><FileCheck className="h-3 w-3" /> Pending document reviews</div>
        <ul className="space-y-2 text-sm">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-b-0">
              <div>
                <div className="text-lifted-text">{d.label} <span className="chip">{d.kind}</span></div>
                <div className="text-xs text-lifted-dim break-all">{d.url}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => review(d.id, "approved")} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]">Approve</button>
                <button onClick={() => review(d.id, "rejected")} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] hover:!text-rose-300">Reject</button>
              </div>
            </li>
          ))}
          {docs.length === 0 && <li className="text-lifted-mute text-xs">No pending document reviews.</li>}
        </ul>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link to="/admin/delivery/dispatch" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-lifted-green" />
            <div>
              <div className="font-anton text-lg uppercase">Dispatch board</div>
              <div className="text-xs text-lifted-dim">Partner-sourced orders appear here on `ready_for_driver`.</div>
            </div>
          </div>
        </Link>
        <Link to="/admin/partners/metrics" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-lifted-green" />
            <div>
              <div className="font-anton text-lg uppercase">Metrics</div>
              <div className="text-xs text-lifted-dim">Fulfillment + handoff rollups per partner.</div>
            </div>
          </div>
        </Link>
      </div>

      {/* Iter74 — Bulk hard-delete confirmation modal */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel max-w-md w-full p-5 border border-rose-400/40">
            <h2 className="font-anton text-xl uppercase text-white mb-1">PERMANENTLY delete {selected.size} partner{selected.size === 1 ? "" : "s"}?</h2>
            <p className="text-sm text-rose-200 mb-4">
              This <strong>permanently removes</strong> the partner records from the database (NOT an archive). Master-admin protected accounts (Brian, Jesse Workman, etc.) will be skipped automatically. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(false)} className="btn-ghost !py-2 !px-3 !text-xs">Cancel</button>
              <button
                data-testid="admin-partners-bulk-delete-confirm"
                onClick={doBulkDelete}
                disabled={busy}
                className="btn-lifted !py-2 !px-3 !text-xs bg-rose-500/80 hover:bg-rose-500"
              >{busy ? "Deleting…" : "Yes, permanently delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
